import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  walkthroughForRoute,
  walkthroughs,
  type WalkthroughKey,
} from '@/lib/walkthroughs';

type WalkthroughStatus = 'in_progress' | 'completed' | 'skipped' | 'dismissed';
type WalkthroughProgress = Record<string, { status: WalkthroughStatus; updatedAt: string }>;

interface OnboardingStateRow {
  walkthrough_progress: WalkthroughProgress | null;
}

async function loadProgress(userId: string): Promise<WalkthroughProgress> {
  const { data } = await supabase
    .from('onboarding_state')
    .select('walkthrough_progress')
    .eq('user_id', userId)
    .maybeSingle<OnboardingStateRow>();
  return (data?.walkthrough_progress ?? {}) as WalkthroughProgress;
}

async function saveProgress(userId: string, key: WalkthroughKey, status: WalkthroughStatus) {
  const current = await loadProgress(userId);
  const next: WalkthroughProgress = {
    ...current,
    [key]: { status, updatedAt: new Date().toISOString() },
  };
  await supabase
    .from('onboarding_state')
    .upsert(
      { user_id: userId, walkthrough_progress: next as any, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' },
    );
}

export function useWalkthrough() {
  const { user } = useAuth();
  const location = useLocation();
  const driverRef = useRef<Driver | null>(null);

  const definition = useMemo(() => walkthroughForRoute(location.pathname), [location.pathname]);

  const start = useCallback(
    async (key?: WalkthroughKey) => {
      const def = key ? walkthroughs[key] : definition;
      if (!def || !user?.id) return;

      // Wait a tick so target elements have mounted.
      await new Promise((r) => setTimeout(r, 200));

      const d = driver({
        showProgress: true,
        animate: true,
        smoothScroll: true,
        allowClose: true,
        progressText: 'Step {{current}} of {{total}}',
        nextBtnText: 'Next →',
        prevBtnText: '← Back',
        doneBtnText: 'Got it',
        steps: def.steps,
        onCloseClick: () => {
          d.destroy();
          void saveProgress(user.id, def.key, 'dismissed');
        },
        onDestroyed: () => {
          driverRef.current = null;
        },
      });

      driverRef.current = d;
      d.drive();
      void saveProgress(user.id, def.key, 'in_progress');

      // Detect natural completion (last step finished).
      const interval = setInterval(() => {
        if (!d.isActive()) {
          clearInterval(interval);
          // If the user clicked through all steps, mark completed.
          // (dismissed is already saved by onCloseClick.)
          const isLast = d.hasNextStep() === false;
          if (isLast) void saveProgress(user.id, def.key, 'completed');
        }
      }, 250);
    },
    [definition, user?.id],
  );

  const reset = useCallback(async () => {
    if (!user?.id) return;
    await supabase
      .from('onboarding_state')
      .upsert(
        { user_id: user.id, walkthrough_progress: {} as any, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' },
      );
  }, [user?.id]);

  // Auto-launch walkthrough on first visit to a covered route, once per page.
  useEffect(() => {
    let cancelled = false;
    if (!user?.id || !definition) return;
    (async () => {
      const progress = await loadProgress(user.id);
      const entry = progress[definition.key];
      if (cancelled) return;
      // Auto-start only if user hasn't seen, dismissed, or completed it.
      if (!entry) {
        void start(definition.key);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, definition?.key]);

  return { start, reset, definition };
}
